/**
 * Strips every non-digit from a phone string.
 *
 * @param phone - Phone in any format, masked or not.
 * @returns The digits alone.
 */
export function whatsAppPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Formats a Brazilian phone for display: `(11) 91234-5678`.
 *
 * Accepts anything — masked, bare digits, with or without the 55 country
 * code — and returns the caller's input untouched when it does not look
 * like a Brazilian number, so a foreign or half-typed value is never
 * mangled into a shape it does not have.
 *
 * The backend stores the linked number verbatim and shards its phone index
 * by a hash of that exact string, so this is strictly a display helper:
 * never send its output over the wire, send `whatsAppPhoneDigits`.
 *
 * Null/undefined-safe: a missing or empty phone yields an empty string
 * instead of throwing, so a malformed/phone-less linkage record can never
 * crash the WhatsApp settings page (Bug #3879).
 *
 * @param phone - Phone in any format, or null/undefined when absent.
 * @returns The masked phone, the trimmed input when it cannot be masked, or
 *   an empty string when the phone is null/undefined/blank.
 */
export function formatWhatsAppPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const national = withoutCountryCode(whatsAppPhoneDigits(phone));
  if (!isNationalLength(national)) return phone.trim();
  const rest = national.slice(2);
  const split = rest.length === 9 ? 5 : 4;
  return `(${national.slice(0, 2)}) ${rest.slice(0, split)}-${rest.slice(split)}`;
}

/**
 * 12 or 13 digits means the 55 country code rode along; drop it.
 */
function withoutCountryCode(digits: string): string {
  return digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;
}

/**
 * A maskable Brazilian number is DDD plus eight or nine digits.
 */
function isNationalLength(national: string): boolean {
  return national.length >= 10 && national.length <= 11;
}

/**
 * Masks a Brazilian phone as it is typed, tolerating partial input.
 *
 * @param phone - The raw field value, possibly mid-typing.
 * @returns The value masked as far as the digits allow.
 */
export function maskWhatsAppPhoneInput(phone: string): string {
  const digits = whatsAppPhoneDigits(phone).slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  const split = rest.length > 8 ? 5 : 4;
  return `(${ddd}) ${rest.slice(0, split)}-${rest.slice(split)}`;
}

/**
 * Normalizes a Brazilian phone number for the wa.me deep link format.
 * Strips non-digits and prepends country code 55 if missing.
 */
function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('55')) return digits;
  return `55${digits}`;
}

/**
 * Builds a WhatsApp deep link (wa.me) with optional pre-filled message.
 *
 * @param phone - Brazilian phone number (any format)
 * @param message - Optional pre-filled message text
 * @returns WhatsApp URL, or empty string if phone is blank
 */
export function buildWhatsAppLink(phone: string, message?: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return '';
  const normalized = normalizePhone(trimmed);
  const base = `https://wa.me/${normalized}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message)}`;
}
